import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatInOrgTz, orgCivilOf, civilToISODate, orgCivilToday, orgAddDays } from '../lib/datetime';
import { Link, useParams, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import CustomerModal from '../components/CustomerModal';
import MarkDeadLeadModal from '../components/MarkDeadLeadModal';
import { AreaSpark, FunnelBars, GaugeArc, RiskDonut } from '../components/Charts';
import { useCrm, LEAD_STAGES, STAGE_META, isClosedStage } from '../lib/crm';
import { dealStageLabel, fmtMoney } from '../lib/deals';
import { useToast } from '../lib/toast';
import { customersService, leadsService, deadLeadsService, tasksService } from '../lib/services';
import { useCustomerNotes } from '../lib/useCustomerNotes';
import { useCurrentUser } from '../lib/currentUser';
import { formatDateTime, relativeDue } from '../lib/datetime';
import NextEventChip from '../components/NextEventChip';
import NextEventModal from '../components/NextEventModal';
import CompleteTaskModal from '../components/CompleteTaskModal';

const TAG_SUGGESTIONS = ['VIP', 'At risk', 'Service', 'Sales', 'Hot lead', 'Finance ready', 'Trade-in', 'Family'];

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCustomer, toggleTag, leads, deals, updateCustomer } = useCrm();
  const { consultantId } = useCurrentUser();
  // Notes are persisted now — the old useCrm notes lived in React state only
  // and vanished on reload.
  const notesApi = useCustomerNotes(id);
  // Tasks / next event on the customer record (E11). Lives on lead_tasks via
  // the customerId filter; nothing new to plumb.
  const [customerTasks, setCustomerTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [nextEventOpen, setNextEventOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [completingTask, setCompletingTask] = useState(null);
  const loadTasks = useCallback(async () => {
    if (!id) return;
    setTasksLoading(true);
    try {
      const rows = await tasksService.list({ customerId: id, page: 0, limit: 200 });
      const list = Array.isArray(rows) ? rows : (rows?.content ?? []);
      setCustomerTasks(list);
    } catch { /* keep what we have */ }
    finally { setTasksLoading(false); }
  }, [id]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  const openTasks = useMemo(
    () => customerTasks
      .filter((t) => t.status === 'OPEN' || t.status === 'IN_PROGRESS')
      .sort((a, b) => new Date(a.dueAt || 8.64e15) - new Date(b.dueAt || 8.64e15)),
    [customerTasks],
  );
  const doneTasks = useMemo(
    () => customerTasks.filter((t) => t.status === 'DONE' || t.status === 'CANCELLED')
      .sort((a, b) => new Date(b.completedAt || b.updatedAt || 0) - new Date(a.completedAt || a.updatedAt || 0)),
    [customerTasks],
  );
  const nextEvent = openTasks[0] || null;
  const { toast } = useToast();
  const [noteText, setNoteText] = useState('');
  const [tab, setTab] = useState('timeline'); // timeline | notes | leads | deals | dead | files
  const [view, setView] = useState('profile'); // profile | dashboard (Customer 360)
  const [editOpen, setEditOpen] = useState(false);
  const [markDeadOpen, setMarkDeadOpen] = useState(false);
  const [deadLeadTargets, setDeadLeadTargets] = useState([]);
  const [deadLeadLoading, setDeadLeadLoading] = useState(false);

  // Live backend-backed conversation timeline + leads for this customer.
  const [liveInteractions, setLiveInteractions] = useState([]);
  const [liveLeads,        setLiveLeads]        = useState([]);
  const [timelineLoading,  setTimelineLoading]  = useState(true);
  const [timelineError,    setTimelineError]    = useState(null);
  const [liveTimeline, setLiveTimeline] = useState([]);

  const customer = getCustomer(id);

  const onEditSave = async (payload) => {
    await updateCustomer(id, payload);
    toast(`Customer "${payload.name || customer?.name}" updated`, { variant: 'success' });
  };

  const loadCustomerTimeline = useCallback(async () => {
    if (!id) return;
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const [ints, lds, evts] = await Promise.all([
        customersService.interactions(id).catch(() => []),
        leadsService.byCustomer(id).catch(() => []),
        customersService.timeline(id).catch(() => []),
      ]);
      setLiveInteractions(Array.isArray(ints) ? ints : []);
      setLiveLeads(Array.isArray(lds) ? lds : []);
      setLiveTimeline(Array.isArray(evts) ? evts : []);
    } catch (e) {
      setTimelineError(e?.message || 'Failed to load history');
      setLiveInteractions([]);
      setLiveLeads([]);
      setLiveTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  }, [id]);

  useEffect(() => { loadCustomerTimeline(); }, [loadCustomerTimeline]);

  // Dead-lead campaign history for this customer — loaded lazily when the
  // tab is opened, then refreshed whenever the customer id changes.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setDeadLeadLoading(true);
    deadLeadsService.targetsForCustomer(id)
      .then(rows => { if (!cancelled) setDeadLeadTargets(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setDeadLeadTargets([]); })
      .finally(() => { if (!cancelled) setDeadLeadLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Prefer backend-backed leads when present; fall back to CrmProvider's
  // in-memory list (which the dashboard also reads) for legacy paths.
  const linkedLeads = useMemo(
    () => (liveLeads.length ? liveLeads : leads.filter(l => l.customerId === id)),
    [liveLeads, leads, id],
  );
  const linkedDeals = useMemo(() => deals.filter(d => d.customerId === id), [deals, id]);
  const customerNotes = notesApi.notes;

  if (!customer) {
    return (
      <>
        <TopBar crumbs={[['Customers', '/customers'], 'Not found']} />
        <div className="app-content">
          <div className="empty-state">
            <div className="icon">?</div>
            <div className="title">Customer not found</div>
            <div className="desc">The customer ID <span className="mono">{id}</span> doesn't exist or was removed.</div>
            <button className="btn primary small" style={{ marginTop: 14 }} onClick={() => navigate('/customers')}>
              ← Back to Customers
            </button>
          </div>
        </div>
      </>
    );
  }

  const onAddNote = async () => {
    if (!noteText.trim()) return;
    const text = noteText.trim();
    setNoteText('');
    try {
      await notesApi.add(text);
      toast('Note saved', { variant: 'success' });
    } catch {
      setNoteText(text); // hand it back rather than lose it
    }
  };

  return (
    <>
      <TopBar
        crumbs={[['Customers', '/customers'], customer.displayName || customer.name]}
        right={
          <>
            <div className="mode-toggle" role="tablist" aria-label="Customer view" style={{ marginRight: 4 }}>
              <button
                type="button"
                className={view === 'profile' ? 'active' : ''}
                onClick={() => setView('profile')}
                role="tab"
                aria-selected={view === 'profile'}
              >
                Profile
              </button>
              <button
                type="button"
                className={view === 'dashboard' ? 'active' : ''}
                onClick={() => setView('dashboard')}
                role="tab"
                aria-selected={view === 'dashboard'}
              >
                360 View
              </button>
            </div>
            <button className="btn small" onClick={() => toast('Calling ' + customer.phone, { variant: 'info' })}>📞 Call</button>
            <button className="btn small" onClick={() => toast('Message draft opened', { variant: 'info' })}>📩 Message</button>
            <button className="btn small" onClick={() => setEditOpen(true)}>✎ Edit details</button>
            {customer.lifecycleStatus !== 'DEAD_LEAD' && (
              <button className="btn small danger" onClick={() => setMarkDeadOpen(true)}>Mark as lost</button>
            )}
            <button className="btn small primary" onClick={() => setTab('notes')}>+ Note</button>
          </>
        }
      />
      {view === 'dashboard' ? (
        <Customer360View
          customer={customer}
          leads={linkedLeads}
          deals={linkedDeals}
          interactions={liveInteractions}
        />
      ) : (
      <>
      {/* ── At-a-glance strip: the state of the customer without reading three columns.
            Next step · live lead · churn · LTV · vehicles. (Information hierarchy — E11.) */}
      {/* `.app-content` is `flex: 1; overflow: auto` — the shell expects exactly
          one of them per page. A second one made the strip a co-equal flex child:
          it split the viewport with the real content, stretched a 47px bar to
          ~440px of empty white above the fold, and gave the page two independent
          scroll areas. Pinned to its natural height, with scrolling left to the
          grid below. */}
      <div className="app-content" style={{ paddingBottom: 0, flex: '0 0 auto', overflow: 'visible' }}>
        <div className="card" style={{ padding: '10px 14px', display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="flex-row" style={{ alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span className="card-label" style={{ margin: 0 }}>NEXT</span>
            {nextEvent ? (
              <button type="button" className="btn small" onClick={() => setCompletingTask(nextEvent)} title="Mark complete">✓</button>
            ) : null}
            <NextEventChip
              nextEvent={nextEvent ? { ...nextEvent, reasonLabel: nextEvent.reasonLabel || nextEvent.type } : null}
              onSet={!nextEvent ? () => { setEditingTask(null); setNextEventOpen(true); } : undefined}
              showOwner
            />
            {/* The right rail used to repeat this card in full, with its own
                Set/Change/Complete. One commitment, one place to change it —
                and this is the copy that stays on screen while you scroll. */}
            {nextEvent && (
              <button
                type="button"
                className="btn small"
                onClick={() => { setEditingTask(nextEvent); setNextEventOpen(true); }}
                title="Change the next step"
              >
                ✎
              </button>
            )}
            <button type="button" className="btn small" onClick={() => setTab('tasks')} title="All tasks for this customer">
              All tasks
            </button>
          </div>
          <span style={{ width: 1, height: 18, background: 'var(--border-color)' }} />
          <div className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>LEAD</span>
            {(() => {
              const live = linkedLeads.find((l) => !isClosedStage(l.stage));
              if (!live) return <span className="mono text-xs text-muted">no open lead</span>;
              const vehicle = live.vehicle || [live.vehicleMakeRequested, live.vehicleModelRequested].filter(Boolean).join(' ') || '—';
              return (
                <Link to={`/leads/${live.id}`} style={{ fontSize: 13, textDecoration: 'none' }}>
                  {vehicle} · <span className="pill">{STAGE_META[live.stage]?.label || live.stage}</span>
                </Link>
              );
            })()}
          </div>
          <span style={{ width: 1, height: 18, background: 'var(--border-color)' }} />
          <div className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>CHURN</span>
            <span className={`risk-pill risk-${customer.churnRisk}`}>{customer.churnRisk}</span>
          </div>
          <div className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>LTV</span>
            <span className="mono" style={{ fontSize: 13 }}>${customer.ltv.toLocaleString()}</span>
          </div>
          <div className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>VEHICLES</span>
            <span className="mono" style={{ fontSize: 13 }}>{customer.vehicles.length}</span>
          </div>
          {/* Consent used to be repeated here. It is stated in full — including
              DNC, which this line omitted — in the identity card on the left,
              and a permission a person must be able to trust is worse for being
              in two places that could disagree. */}
        </div>
      </div>
      <div className="app-content" style={{ display: 'grid', gridTemplateColumns: '280px 1fr 280px', gap: 14, paddingTop: 12 }}>
        {/* ── Left column: profile + risk + vehicles + consent.
              Pinned while the timeline scrolls — who you are looking at should
              not leave the screen fifty messages into their history. ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 0, alignSelf: 'start' }}>
          <div className="card">
            <div className="flex-row" style={{ marginBottom: 10 }}>
              <span className="avatar-pill" style={{ width: 56, height: 56, fontSize: 16 }}>{customer.initials}</span>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, fontStyle: customer.hasRealName ? undefined : 'italic', color: customer.hasRealName ? undefined : 'var(--text-muted)' }}>{customer.displayName || customer.name}</div>
                <div className="mono text-xs text-muted">
                  Customer since {customer.since} · LTV ${customer.ltv.toLocaleString()}
                </div>
              </div>
            </div>
            <div className="mono text-xs text-muted" style={{ lineHeight: 1.8 }}>
              {customer.phone || <span style={{ fontStyle: 'italic' }}>no phone on file</span>} · {customer.consents.sms ? '✓ SMS' : '✗ SMS'} consent{consentNote(customer.consentDetail?.sms)}<br />
              {customer.email || <span style={{ fontStyle: 'italic' }}>no email on file</span>} · {customer.consents.email ? '✓ Email' : '✗ Email'} consent{consentNote(customer.consentDetail?.email)}<br />
              WhatsApp · {customer.consents.whatsapp ? '✓ opted-in' : '✗ not opted-in'}{consentNote(customer.consentDetail?.whatsapp)}<br />
              DNC: {customer.consents.dnc ? '⚠ on register' : '✗ not on register'}
            </div>
          </div>

          {/* Churn was set at 28px and 800 weight — bigger than the customer's
              own name — while lifetime value sat in 11px body text. A LOW risk
              is the least actionable thing on the page; it earns a pill, and the
              reason beside it, not a headline. The number grows in importance
              only when it is HIGH, which the colour and the action carry. */}
          <div className="card">
            <div className="flex-between" style={{ marginBottom: 6 }}>
              <span className="card-label" style={{ margin: 0 }}>CHURN RISK</span>
              <span className={`risk-pill risk-${customer.churnRisk}`}>{customer.churnRisk}</span>
            </div>
            <div className="mono text-xs text-muted">
              {customer.churnReason || 'No risk signals on this customer.'}
            </div>
            {customer.churnRisk === 'HIGH' && (
              <button
                className="btn small danger"
                style={{ marginTop: 8 }}
                onClick={() => toast('Manager intervention scheduled', { variant: 'warning' })}
              >
                Manager intervene →
              </button>
            )}
          </div>

          {/* Applied and available tags were one undifferentiated row, so the
              only thing separating "this customer is VIP" from "you could call
              them VIP" was a tick versus a plus. Split them: what is true about
              the customer reads first, what you could add is demoted. */}
          <div className="card">
            <div className="card-label">TAGS</div>
            {(customer.tags || []).length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(customer.tags || []).map(t => (
                  <span
                    key={t}
                    onClick={() => toggleTag(customer.id, t)}
                    className="pill accent filled"
                    style={{ cursor: 'pointer' }}
                    title="Click to remove"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mono text-xs text-muted">None yet</div>
            )}
            {TAG_SUGGESTIONS.some(t => !(customer.tags || []).includes(t)) && (
              <>
                <div className="mono text-xs text-muted" style={{ margin: '10px 0 4px' }}>ADD</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {TAG_SUGGESTIONS.filter(t => !(customer.tags || []).includes(t)).map(t => (
                    <span
                      key={t}
                      onClick={() => toggleTag(customer.id, t)}
                      className="pill"
                      style={{ cursor: 'pointer', opacity: 0.7 }}
                    >
                      + {t}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="card">
            <div className="card-label">VEHICLES</div>
            {customer.vehicles.length === 0 ? (
              <div className="mono text-xs text-muted">No vehicles on file</div>
            ) : (
              customer.vehicles.map(v => (
                <div key={v.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{v.label}</div>
                  <div className="mono text-xs text-muted">{v.vin} · {v.status}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Center column: tabs ── */}
        <div className="card animate-in" style={{ padding: 0 }}>
          <div className="tab-bar" style={{ padding: '0 14px' }}>
            {[
              ['timeline', 'Timeline',  liveTimeline.length],
              ['tasks',    'Tasks',     openTasks.length],
              ['notes',    'Notes',     customerNotes.length],
              ['leads',    'Leads',     linkedLeads.length],
              ['deals',    'Deals',     linkedDeals.length],
              ['dead',     'Lost leads', deadLeadTargets.length],
              ['files',    'Files',     0],
            ].map(([k, label, count]) => (
              <div
                key={k}
                onClick={() => setTab(k)}
                className={`tab-item ${tab === k ? 'active' : ''}`}
              >
                {label}{count ? <span style={{ marginLeft: 6, opacity: 0.7 }}>({count})</span> : null}
              </div>
            ))}
          </div>
          <div style={{ padding: 14 }}>
            {tab === 'timeline' && (
              <ActivityTimeline
                events={liveTimeline}
                loading={timelineLoading}
                error={timelineError}
                onRetry={loadCustomerTimeline}
              />
            )}
            {tab === 'notes' && (
              <NotesPanel
                notes={customerNotes}
                loading={notesApi.loading}
                error={notesApi.error}
                onRetry={notesApi.reload}
                value={noteText}
                onChange={setNoteText}
                onAdd={onAddNote}
                onDelete={async (noteId) => { try { await notesApi.remove(noteId); toast('Note deleted', { variant: 'info' }); } catch { /* toasted in hook */ } }}
                onTogglePin={notesApi.togglePin}
              />
            )}
            {tab === 'tasks' && (
              <TasksPanel
                open={openTasks}
                done={doneTasks}
                loading={tasksLoading}
                onNew={() => { setEditingTask(null); setNextEventOpen(true); }}
                onEdit={(t) => { setEditingTask(t); setNextEventOpen(true); }}
                onComplete={setCompletingTask}
                onSnooze={async (t) => {
                  const base = Math.max(new Date(t.dueAt || Date.now()).getTime(), Date.now());
                  try { await tasksService.update(t.id, { dueAt: new Date(base + 86_400_000).toISOString() }); toast('Snoozed a day', { variant: 'success' }); loadTasks(); }
                  catch (e) { toast(`Could not snooze: ${e?.message || 'unknown error'}`, { variant: 'danger' }); }
                }}
              />
            )}
            {tab === 'leads' && <LinkedLeads leads={linkedLeads} />}
            {tab === 'deals' && <LinkedDeals deals={linkedDeals} />}
            {tab === 'dead'  && (
              <DeadLeadHistory targets={deadLeadTargets} loading={deadLeadLoading} />
            )}
            {tab === 'files' && (
              <div className="empty-state">
                <div className="icon">📁</div>
                <div className="title">No files attached</div>
                <div className="desc">Upload service records, finance docs or trade-in photos here. (Coming soon.)</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right column: insights. The NEXT EVENT card that used to head this
              column has gone: it duplicated the strip above, which is always on
              screen, while this copy scrolled away. Two Set buttons for one
              commitment is how a lead ends up with two next steps. Detail that
              the chip cannot carry — reason, exact due, owner — is one click
              away under All tasks. ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 0, alignSelf: 'start' }}>
          <div className="card">
            <div className="card-label">LIFETIME</div>
            <div className="mono text-xs" style={{ lineHeight: 2, color: 'var(--text-secondary)' }}>
              Total spend: <b>${customer.stats.totalSpend.toLocaleString()}</b><br />
              Service ROs: {customer.stats.ros}<br />
              Avg RO: ${customer.stats.avgRO}<br />
              Vehicles bought: {customer.stats.vehiclesBought}<br />
              Referrals: {customer.stats.referrals}
            </div>
          </div>

          <div className="card">
            <div className="card-label">PREFERENCES</div>
            <div className="mono text-xs text-muted" style={{ lineHeight: 2 }}>
              Channel: {customer.preferences.channel}<br />
              Call window: {customer.preferences.callWindow}<br />
              Language: {customer.preferences.lang}<br />
              Marketing: {customer.preferences.marketing ? '✓' : '✗'}<br />
              Service reminders: {customer.preferences.serviceReminders ? '✓' : '✗'}
            </div>
          </div>

          <div className="card">
            <div className="card-label">QUICK LINKS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Link className="btn small" to="/leads">View all leads</Link>
              <Link className="btn small" to="/deals">View deals pipeline</Link>
              <Link className="btn small" to="/service">Service Advisor</Link>
              <Link className="btn small" to="/compliance">Audit trail</Link>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      <NextEventModal
        open={nextEventOpen}
        customerId={customer.id}
        leadId={editingTask?.leadId || linkedLeads.find((l) => !isClosedStage(l.stage))?.id || null}
        task={editingTask}
        defaultAssigneeId={editingTask?.assigneeId || consultantId}
        onClose={() => { setNextEventOpen(false); setEditingTask(null); }}
        onSaved={() => { setNextEventOpen(false); setEditingTask(null); loadTasks(); }}
      />
      <CompleteTaskModal
        open={!!completingTask}
        task={completingTask}
        lead={completingTask?.leadId ? (linkedLeads.find((l) => l.id === completingTask.leadId) || null) : null}
        convertsToAppointment={!!completingTask?.convertsToAppointment}
        onClose={() => setCompletingTask(null)}
        onCompleted={() => { setCompletingTask(null); loadTasks(); }}
      />
      <CustomerModal
        open={editOpen}
        mode="edit"
        customer={customer}
        onClose={() => setEditOpen(false)}
        onSave={onEditSave}
      />

      <MarkDeadLeadModal
        open={markDeadOpen}
        mode="customer"
        customerId={id}
        onClose={() => setMarkDeadOpen(false)}
        onMarked={() => {
          toast('Customer marked as dead — moved to the lost-lead pool', { variant: 'success' });
          navigate('/dead-leads');
        }}
      />
    </>
  );
}

/* ────────────────────────────────────────────────────────────
   Customer 360 — a visual dashboard view of one customer, toggled
   from the profile view. Everything is derived from data already
   on the page (customer stats, linked leads/deals, interactions).
   ──────────────────────────────────────────────────────────── */
function Customer360View({ customer, leads = [], deals = [], interactions = [] }) {
  const s = customer.stats || {};

  const riskValue = customer.churnRisk === 'HIGH' ? 0.85
    : customer.churnRisk === 'MED' ? 0.5 : 0.18;
  const riskColor = customer.churnRisk === 'HIGH' ? 'var(--danger)'
    : customer.churnRisk === 'MED' ? 'var(--warning)' : 'var(--success)';

  // Lead funnel — this customer's leads bucketed by pipeline stage.
  const stageSegments = LEAD_STAGES
    .filter((st) => st !== 'LOST')
    .map((st) => ({
      l: STAGE_META[st]?.short || st,
      v: leads.filter((l) => l.stage === st).length,
      c: STAGE_META[st]?.color || 'var(--text-tertiary)',
    }))
    .filter((x) => x.v > 0);

  // Deals split.
  const openDeals = deals.filter((d) => !isClosedStage(d.stage));
  const wonDeals = deals.filter((d) => d.stage === 'DELIVERED');
  const openDealValue = openDeals.reduce((a, d) => a + (d.value || 0), 0);

  // Engagement trend — interactions per month, oldest → newest.
  const byMonth = {};
  interactions.forEach((r) => {
    const t = r.occurredAt || r.createdAt;
    if (!t) return;
    const key = String(t).slice(0, 7);
    byMonth[key] = (byMonth[key] || 0) + 1;
  });
  const months = Object.keys(byMonth).sort();
  const engagement = months.map((m) => byMonth[m]);

  // Channel mix donut.
  const channelColors = {
    WHATSAPP: 'var(--success)', SMS: 'var(--accent-primary)',
    VOICE: 'var(--warning)', EMAIL: 'var(--info)',
  };
  const channelCounts = {};
  interactions.forEach((r) => {
    const ch = (r.channel || 'OTHER').toUpperCase();
    channelCounts[ch] = (channelCounts[ch] || 0) + 1;
  });
  const channelSegments = Object.entries(channelCounts)
    .map(([l, v]) => ({ l, v, c: channelColors[l] || 'var(--text-tertiary)' }))
    .sort((a, b) => b.v - a.v);

  const kpis = [
    ['Lifetime value',  fmtMoney(customer.ltv || 0),                              'var(--accent-primary)'],
    ['Total spend',     fmtMoney(s.totalSpend || 0),                              'var(--success)'],
    ['Vehicles owned',  String(s.vehiclesBought ?? (customer.vehicles?.length || 0)), 'var(--info)'],
    ['Service ROs',     String(s.ros || 0),                                       'var(--text-primary)'],
  ];

  const recent = [...interactions]
    .sort((a, b) => new Date(b.occurredAt || b.createdAt || 0) - new Date(a.occurredAt || a.createdAt || 0))
    .slice(0, 6);

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {kpis.map(([label, value, color], i) => (
          <div key={i} className="card animate-in" style={{ animationDelay: `${i * 0.04}s` }}>
            <div className="card-label">{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Engagement trend + churn risk */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14 }}>
        <div className="card animate-in">
          <div className="flex-between" style={{ marginBottom: 10 }}>
            <span className="card-label" style={{ margin: 0 }}>ENGAGEMENT TREND</span>
            <span className="mono text-xs text-muted">{interactions.length} interactions</span>
          </div>
          {engagement.length > 1 ? (
            <>
              <AreaSpark values={engagement} w={520} h={90} color="var(--accent-primary)" />
              <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
                {months[0]} → {months[months.length - 1]} · monthly conversation volume
              </div>
            </>
          ) : (
            <div className="empty-state" style={{ padding: '20px 8px' }}>
              <div className="title">Not enough history yet</div>
              <div className="desc">Conversation volume appears here once there are a few months of activity.</div>
            </div>
          )}
        </div>

        <div className="card animate-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="card-label" style={{ alignSelf: 'flex-start' }}>CHURN RISK</div>
          <GaugeArc value={riskValue} w={220} h={110} color={riskColor} label={customer.churnRisk} />
          <div className="mono text-xs text-muted" style={{ marginTop: 6, textAlign: 'center' }}>
            {customer.churnReason || 'No risk drivers flagged'}
          </div>
        </div>
      </div>

      {/* Lead funnel + deals + channel mix */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <div className="card animate-in">
          <div className="card-label">LEAD PIPELINE</div>
          {stageSegments.length ? (
            <FunnelBars segments={stageSegments} w={240} h={Math.max(48, stageSegments.length * 22)} />
          ) : (
            <div className="mono text-xs text-muted" style={{ marginTop: 8 }}>No active leads for this customer.</div>
          )}
        </div>

        <div className="card animate-in">
          <div className="card-label">DEALS</div>
          <div style={{ display: 'flex', gap: 16, marginTop: 4, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent-primary)' }}>{openDeals.length}</div>
              <div className="mono text-xs text-muted">open · {fmtMoney(openDealValue)}</div>
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--success)' }}>{wonDeals.length}</div>
              <div className="mono text-xs text-muted">won</div>
            </div>
          </div>
          {deals.slice(0, 4).map((d) => (
            <div key={d.id} className="flex-between" style={{ padding: '5px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 12 }}>
              <span className="mono text-xs text-muted">{dealStageLabel(d.stage)}</span>
              <span>{fmtMoney(d.value || 0)}</span>
            </div>
          ))}
          {deals.length === 0 && <div className="mono text-xs text-muted">No deals yet.</div>}
        </div>

        <div className="card animate-in">
          <div className="card-label">CHANNEL MIX</div>
          {channelSegments.length ? (
            <RiskDonut segments={channelSegments} w={240} h={90} />
          ) : (
            <div className="mono text-xs text-muted" style={{ marginTop: 8 }}>No conversations logged yet.</div>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="card animate-in">
        <div className="flex-between" style={{ marginBottom: 10 }}>
          <span className="card-label" style={{ margin: 0 }}>RECENT ACTIVITY</span>
        </div>
        {recent.length === 0 ? (
          <div className="mono text-xs text-muted">No recent conversations.</div>
        ) : (
          recent.map((r, i) => {
            const channel = (r.channel || 'NOTE').toUpperCase();
            const dir = (r.direction || '').toUpperCase();
            return (
              <div key={r.id || i} className="flex-row" style={{ gap: 12, padding: '8px 0', borderBottom: i === recent.length - 1 ? 'none' : '1px solid var(--border-subtle)' }}>
                <span className="mono text-xs text-muted" style={{ width: 120, flexShrink: 0 }}>{fmtTs(r.occurredAt || r.createdAt)}</span>
                <span className={`pill ${CHANNEL_TONE[channel] || ''} ${CHANNEL_TONE[channel] ? 'filled' : ''}`} style={{ flexShrink: 0 }}>{channel}</span>
                <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {dir === 'INBOUND' ? '⇠ ' : dir === 'OUTBOUND' ? '⇢ ' : ''}{r.body || r.transcript || '—'}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ── sub-panels ── */

const CHANNEL_TONE = {
  WHATSAPP: 'success',
  SMS:      'accent',
  VOICE:    'warning',
  EMAIL:    '',
};

/** " (web form, 24 May 2026)" — where and when a consent was granted or revoked. */
function consentNote(d) {
  if (!d) return '';
  const src = {
    WEB_FORM: 'web form', STOP_SMS: 'STOP reply', STOP_KEYWORD: 'STOP reply',
  }[d.source] || String(d.source || '').toLowerCase().replace(/_/g, ' ');
  const when = d.at ? new Date(d.at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const state = d.state === 'REVOKED' ? 'opted out' : 'granted';
  return ` (${state}${src ? ` · ${src}` : ''}${when ? ` · ${when}` : ''})`;
}

function fmtTs(iso) {
  return formatInOrgTz(iso, {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Time only — the day is carried by the group header above the row. */
function fmtTime(iso) {
  return formatInOrgTz(iso, { hour: '2-digit', minute: '2-digit' }, '');
}

/**
 * Stable per-day key in the DEALERSHIP's timezone, not the viewer's — two
 * messages either side of local midnight in Sydney must group together even
 * when the person reading is in India.
 */
function dayKeyOf(iso) {
  const civil = orgCivilOf(iso);
  return civil ? civilToISODate(civil) : 'unknown';
}

/** "Today" / "Yesterday" / "Mon, 01 June 2026". */
function dayLabelOf(iso) {
  const civil = orgCivilOf(iso);
  if (!civil) return 'Undated';
  const key = civilToISODate(civil);
  const today = orgCivilToday();
  if (key === civilToISODate(today)) return 'Today';
  if (key === civilToISODate(orgAddDays(today, -1))) return 'Yesterday';
  return formatInOrgTz(iso, { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
}

/**
 * Live conversation timeline rendered from backend Interaction rows
 * (every WhatsApp / SMS / voice INBOUND + OUTBOUND across all leads).
 * Shape per row: { id, leadId, channel, direction, body, transcript,
 *                  occurredAt, sentimentScore, … }.
 */
/**
 * T088 — every activity on this customer, not just conversations: reads
 * GET /v1/customers/{id}/timeline (interactions + appointments + tasks +
 * leads, merged newest-first server-side). Was ConversationTimeline,
 * conversations-only; the day-grouped layout below is unchanged, only the
 * per-row rendering now branches on `kind` so appointments/tasks/leads get
 * their own badge instead of being forced through the chat-pill treatment.
 */
function ActivityTimeline({ events, loading, error, onRetry }) {
  if (loading) {
    return (
      <div className="empty-state" style={{ padding: 28 }}>
        <div className="icon">⏳</div>
        <div className="title">Loading activity…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="empty-state" style={{ padding: 28 }}>
        <div className="icon">⚠</div>
        <div className="title">Couldn't load history</div>
        <div className="desc">{error}</div>
        <button className="btn small" style={{ marginTop: 10 }} onClick={onRetry}>Try again</button>
      </div>
    );
  }
  if (!events || events.length === 0) {
    return (
      <div className="empty-state" style={{ padding: 28 }}>
        <div className="icon">💬</div>
        <div className="title">No activity yet</div>
        <div className="desc">
          Conversations, appointments, tasks and leads for this customer will show here as they happen.
        </div>
      </div>
    );
  }

  // Grouped by day, newest day first, with a sticky header per day. Fifty
  // undifferentiated rows all reading "01 June" is a wall; the day is the unit
  // a person actually scans by ("what happened Tuesday"), so it becomes the
  // structure rather than a repeated column. Every row then only has to carry
  // the time, which frees the space the date was using.
  const groups = [];
  for (const row of events) {
    const key = dayKeyOf(row.at);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(row);
    else groups.push({ key, label: dayLabelOf(row.at), rows: [row] });
  }

  return (
    <div>
      {groups.map((g) => (
        <div key={g.key}>
          <div
            className="mono text-xs"
            style={{
              position: 'sticky', top: 0, zIndex: 2,
              background: 'var(--bg-secondary)',
              borderBottom: '1px solid var(--border-color)',
              // No top margin: any gap above a sticky header is a window the
              // rows underneath show through as they scroll past.
              padding: '9px 0 5px', marginTop: 0,
              color: 'var(--text-secondary)', fontWeight: 600, letterSpacing: '.04em',
            }}
          >
            {g.label}
            <span className="text-muted" style={{ fontWeight: 400 }}>
              {' · '}{g.rows.length} {g.rows.length === 1 ? 'event' : 'events'}
            </span>
          </div>
          {g.rows.map((row, i) => {
            const isLast = i === g.rows.length - 1;
            const kind = row.kind || 'INTERACTION';
            // INTERACTION rows still get the richer chat treatment (channel
            // pill, inbound/outbound tint) — the backend bakes those into
            // `title` as "DIRECTION CHANNEL", e.g. "INBOUND WHATSAPP".
            const isConversation = kind === 'INTERACTION';
            const [direction = '', channel = ''] = isConversation ? (row.title || '').split(' ') : [];
            const inbound = direction === 'INBOUND';
            const tone = CHANNEL_TONE[channel] || '';
            const meta = KIND_META[kind] || KIND_META.LEAD;
            return (
              <div
                key={row.refId || `${g.key}-${i}`}
                style={{
                  display: 'flex', gap: 12, padding: '9px 10px 9px 0',
                  borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)',
                  // Who spoke has to survive a fast scroll. A 3px rail alone was
                  // too quiet against 50 near-identical rows, so what the
                  // customer said also gets a faint ground of its own.
                  background: inbound ? 'color-mix(in srgb, var(--accent) 5%, transparent)' : undefined,
                }}
              >
                <div className="mono text-xs text-muted" style={{ width: 58, flexShrink: 0, paddingTop: 1 }}>
                  {fmtTime(row.at)}
                </div>
                {/* A 3px rail carries direction for conversations; other kinds
                    get a neutral rail since "inbound/outbound" doesn't apply. */}
                <div
                  aria-hidden="true"
                  style={{
                    width: 3, flexShrink: 0, borderRadius: 2,
                    background: isConversation ? (inbound ? 'var(--accent)' : 'var(--border-color)') : 'var(--border-color)',
                    opacity: isConversation ? (inbound ? 1 : 0.7) : 0.5,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex-row" style={{ gap: 6, alignItems: 'center', marginBottom: 3 }}>
                    {isConversation ? (
                      <>
                        <span className={`pill ${tone} ${tone ? 'filled' : ''}`}>{channel || 'NOTE'}</span>
                        <span className="mono text-xs text-muted">
                          {inbound ? 'from customer' : direction === 'OUTBOUND' ? 'from dealership' : direction}
                        </span>
                      </>
                    ) : (
                      <span className="pill">{meta.icon} {meta.label}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {isConversation ? (row.detail || '—') : row.title}
                  </div>
                  {!isConversation && row.detail && (
                    <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{row.detail}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

const KIND_META = {
  INTERACTION: { icon: '💬', label: 'Conversation' },
  APPOINTMENT: { icon: '📅', label: 'Appointment' },
  TASK:        { icon: '✓',  label: 'Task' },
  LEAD:        { icon: '🚗', label: 'Lead' },
};

function NotesPanel({ notes, loading, error, onRetry, value, onChange, onAdd, onDelete, onTogglePin }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && (
        <div className="flex-between mono text-xs" style={{ color: 'var(--danger)' }}>
          <span>Couldn't load notes from the server.</span>
          <button type="button" className="btn small" onClick={onRetry}>Retry</button>
        </div>
      )}
      {loading && !notes.length && <div className="mono text-xs text-muted">loading…</div>}
      <div>
        <textarea
          className="note-input"
          placeholder="Add a note about this customer… (Ctrl+Enter to save)"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onAdd(); }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <button className="btn small primary" onClick={onAdd} disabled={!value.trim()}>Save note</button>
        </div>
      </div>
      {notes.length === 0 && !loading ? (
        <div className="empty-state" style={{ padding: '24px 8px' }}>
          <div className="icon">✎</div>
          <div className="title">No notes yet</div>
          <div className="desc">Capture context — call summaries, manager actions, customer asks.</div>
        </div>
      ) : (
        notes.map(n => (
          <div key={n.id} className="note-card" style={{ opacity: n._pending ? 0.6 : 1, borderLeft: n.pinned ? '3px solid var(--accent-primary)' : undefined }}>
            <div className="note-meta">
              <span>{n.authorName || n.author || '—'}</span>
              <span>·</span>
              <span>{n.createdAt ? formatDateTime(n.createdAt) : n.ts}</span>
              {n.pinned && <><span>·</span><span style={{ color: 'var(--accent-primary)' }}>pinned</span></>}
              {onTogglePin && !n._pending && (
                <button type="button" className="btn small" style={{ marginLeft: 'auto' }} onClick={() => onTogglePin(n.id)}>
                  {n.pinned ? 'Unpin' : 'Pin'}
                </button>
              )}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{n.body ?? n.text}</div>
            {!n._pending && <button className="note-delete" onClick={() => onDelete(n.id)} aria-label="Delete note">×</button>}
          </div>
        ))
      )}
    </div>
  );
}

function TasksPanel({ open, done, loading, onNew, onEdit, onComplete, onSnooze }) {
  const Row = ({ t, closed }) => {
    const rel = relativeDue(t.dueAt);
    return (
      <div className="flex-between" style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="flex-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, textDecoration: closed ? 'line-through' : 'none', opacity: closed ? 0.7 : 1 }}>{t.title}</span>
            {t.reasonLabel && <span className="pill">{t.reasonLabel}</span>}
            {closed && t.completionOutcome && <span className="pill">{String(t.completionOutcome).replace(/_/g, ' ').toLowerCase()}</span>}
          </div>
          <div className="mono text-xs text-muted" style={{ marginTop: 3 }}>
            {closed
              ? `done ${t.completedAt ? formatDateTime(t.completedAt) : ''}`
              : <span style={{ color: rel.overdue ? 'var(--danger)' : undefined, fontWeight: rel.overdue ? 600 : 400 }}>{t.dueAt ? `${formatDateTime(t.dueAt)} · ${rel.label}` : 'no due date'}</span>}
            {t.assigneeName ? ` · ${t.assigneeName}` : ''}
          </div>
        </div>
        {!closed && (
          <div className="flex-row" style={{ gap: 6, flexShrink: 0 }}>
            <button type="button" className="btn small primary" onClick={() => onComplete(t)}>✓</button>
            <button type="button" className="btn small" onClick={() => onSnooze(t)}>Snooze</button>
            <button type="button" className="btn small" onClick={() => onEdit(t)}>✎</button>
          </div>
        )}
      </div>
    );
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="flex-between">
        <span className="mono text-xs text-muted">{loading ? 'loading…' : `${open.length} open`}</span>
        <button type="button" className="btn small primary" onClick={onNew}>+ New task</button>
      </div>
      {open.length === 0 && !loading ? (
        <div className="empty-state" style={{ padding: '20px 8px' }}>
          <div className="icon">✓</div>
          <div className="title">No open tasks</div>
          <div className="desc">Every customer with a live lead should have a next step.</div>
        </div>
      ) : open.map((t) => <Row key={t.id} t={t} />)}
      {done.length > 0 && (
        <>
          <div className="card-label" style={{ marginTop: 8 }}>COMPLETED</div>
          {done.slice(0, 10).map((t) => <Row key={t.id} t={t} closed />)}
        </>
      )}
    </div>
  );
}

function LinkedLeads({ leads }) {
  if (leads.length === 0) {
    return <div className="empty-state"><div className="icon">⚡</div><div className="title">No leads</div><div className="desc">No active or historic leads linked to this customer.</div></div>;
  }
  return (
    <div>
      {leads.map(l => {
        // Accept both backend Lead rows and CrmProvider in-memory rows.
        const vehicle = l.vehicle
          || [l.vehicleMakeRequested, l.vehicleModelRequested].filter(Boolean).join(' ')
          || '—';
        const source = l.source || '—';
        const stage  = l.stage  || '—';
        const score  = l.score ?? 0;
        const tone   = l.tone || '';
        const when   = l.age || (l.lastActivityAt ? fmtTs(l.lastActivityAt) : (l.createdAt ? fmtTs(l.createdAt) : ''));
        return (
          <Link
            key={l.id}
            to={`/leads/${l.id}`}
            className="flex-row"
            style={{
              padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
              textDecoration: 'none', color: 'inherit',
            }}
          >
            <span className={`pill ${tone} ${tone ? 'filled' : ''}`}>{stage}</span>
            <span style={{ flex: 1, fontSize: 13 }}>{vehicle}</span>
            <span className="mono text-xs text-muted">{source}{when ? ` · ${when}` : ''}</span>
            <span className="mono text-xs" style={{ color: score >= 80 ? 'var(--success)' : score < 40 ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: 700 }}>{score}</span>
          </Link>
        );
      })}
    </div>
  );
}

function LinkedDeals({ deals }) {
  if (deals.length === 0) {
    return (
      <div className="empty-state">
        <div className="icon">◎</div>
        <div className="title">No deals</div>
        <div className="desc">
          This customer has no lead in a commercial stage yet. A lead becomes a deal
          once it reaches Quoting.
        </div>
      </div>
    );
  }
  return (
    <div>
      {deals.map(d => (
        <Link
          key={d.id}
          to={`/deals/${d.id}`}
          className="flex-row"
          style={{
            padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
            textDecoration: 'none', color: 'inherit', gap: 10, alignItems: 'center',
          }}
        >
          <span className={`pill ${d.stage === 'DELIVERED' ? 'success' : d.stage === 'LOST' ? 'danger' : 'accent'} filled`}>
            {dealStageLabel(d.stage)}
          </span>
          <span style={{ flex: 1, fontSize: 13 }}>{d.title}</span>
          <span className="deal-value">
            {d.valueIsEstimated ? '~' : ''}{fmtMoney(d.value)}
          </span>
          <span className="deal-prob">{d.probability}%</span>
        </Link>
      ))}
    </div>
  );
}

// Dead-lead campaign history for a single customer. Driven by the
// /v1/dead-leads/customers/{id}/targets endpoint — rows are ordered
// newest-first by the backend.
const DEAD_LEAD_STATE_TONES = {
  QUEUED:      'accent',
  CONTACTED:   'accent',
  REPLIED:     'success',
  CONVERTED:   'success',
  OPT_OUT:     'warning',
  DNC_BLOCKED: 'danger',
  FAILED:      'danger',
};

function DeadLeadHistory({ targets, loading }) {
  if (loading) {
    return <div className="text-muted text-xs" style={{ padding: 14 }}>Loading…</div>;
  }
  if (!targets || targets.length === 0) {
    return (
      <div className="empty-state">
        <div className="icon">🎯</div>
        <div className="title">No lost-lead history</div>
        <div className="desc">
          This customer hasn't been part of any reactivation campaign yet. The
          nightly eligibility scan runs at 02:00 AEST.
        </div>
      </div>
    );
  }
  return (
    <div>
      {targets.map(t => {
        const tone = DEAD_LEAD_STATE_TONES[t.state] || 'accent';
        const stepStr = `step ${t.currentStep ?? 0}`;
        const channel = t.lastChannel ? `· ${t.lastChannel.toLowerCase()}` : '';
        const touched = formatInOrgTz(t.lastTouchedAt, {
          month: 'short', day: 'numeric', year: 'numeric',
        });
        return (
          <div key={t.id} className="flex-row"
               style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
            <span className={`pill ${tone} filled`}>{t.state}</span>
            <span style={{ flex: 1, fontSize: 13 }}>
              Campaign <span className="mono text-xs text-muted">{(t.campaignId || '').slice(0, 8)}…</span>
            </span>
            <span className="mono text-xs text-muted">
              {stepStr} {channel} · {t.touchCount ?? 0} touches
            </span>
            <span className="mono text-xs text-muted">{touched}</span>
            {t.revivalLeadId && (
              <Link to={`/leads/${t.revivalLeadId}`}
                    className="mono text-xs"
                    style={{ marginLeft: 8, color: 'var(--accent)' }}>
                → revival lead
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}
